import CryptoJS from 'crypto-js';

const getEncryptionKey = (): string => import.meta.env.VITE_PUBLIC_ENCRYPTION_KEY || '';

const padKey = (key: string): CryptoJS.lib.WordArray => {
  let keyBytes = CryptoJS.enc.Utf8.parse(key);

  if (keyBytes.sigBytes < 32) {
    const padding = ' '.repeat(32 - keyBytes.sigBytes);
    keyBytes = CryptoJS.enc.Utf8.parse(key + padding);
  }

  return keyBytes;
};

const stripTrailingControlCharacters = (value: string): string => {
  let end = value.length;

  while (end > 0) {
    const code = value.charCodeAt(end - 1);
    if (code <= 31 || (code >= 127 && code <= 159)) {
      end -= 1;
    } else {
      break;
    }
  }

  return value.slice(0, end);
};

/** AES-256-CBC + PKCS7 with wire format Base64(16-byte IV + ciphertext). */
export const encrypt = (data: unknown): string => {
  const key = getEncryptionKey();

  if (!key) {
    throw new Error('VITE_PUBLIC_ENCRYPTION_KEY is not configured');
  }

  try {
    const serialized = typeof data === 'string' ? data : JSON.stringify(data);

    if (typeof serialized !== 'string') {
      throw new Error('Request body cannot be serialized');
    }

    const iv = CryptoJS.lib.WordArray.random(16);
    const encrypted = CryptoJS.AES.encrypt(serialized, padKey(key), {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return CryptoJS.enc.Base64.stringify(iv.clone().concat(encrypted.ciphertext));
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Encryption failed: ${message}`);
  }
};

/** Decrypt Base64(IV + ciphertext), returning parsed JSON when possible. */
export const decrypt = <T = unknown>(encryptedData: string): T => {
  const key = getEncryptionKey();

  if (!key) {
    throw new Error('VITE_PUBLIC_ENCRYPTION_KEY is not configured');
  }

  try {
    let base64Data = encryptedData.trim().replace(/["'\n\r]/g, '');
    const missingPadding = base64Data.length % 4;

    if (missingPadding) {
      base64Data += '='.repeat(4 - missingPadding);
    }

    const combinedData = CryptoJS.enc.Base64.parse(base64Data);

    if (combinedData.sigBytes < 16) {
      throw new Error('Encrypted data is too short to contain an IV');
    }

    const iv = CryptoJS.lib.WordArray.create(combinedData.words.slice(0, 4), 16);
    const ciphertext = CryptoJS.lib.WordArray.create(
      combinedData.words.slice(4),
      combinedData.sigBytes - 16
    );

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext } as CryptoJS.lib.CipherParams,
      padKey(key),
      {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    const decryptedString = stripTrailingControlCharacters(decrypted.toString(CryptoJS.enc.Utf8));

    if (!decryptedString) {
      throw new Error('Decryption resulted in an empty string');
    }

    try {
      return JSON.parse(decryptedString) as T;
    } catch {
      return decryptedString as T;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Decryption failed: ${message}`);
  }
};

export const decryptResponse = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return decrypt(value);
  }

  if (
    value !== null &&
    typeof value === 'object' &&
    'encrypted' in value &&
    typeof value.encrypted === 'string'
  ) {
    return decrypt(value.encrypted);
  }

  return value;
};
